import React, { useEffect, useState } from "react";
import { Button, Card, Flex, Text } from "@radix-ui/themes";
import { Cross1Icon, ExclamationTriangleIcon } from "@radix-ui/react-icons";
import SVGIcon from "@/app/assets/Icons";

const OfflineIndicator: React.FC = () => {
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const [showIndicator, setShowIndicator] = useState<boolean>(false);
  const [wasOffline, setWasOffline] = useState<boolean>(false);
  const [isClient, setIsClient] = useState<boolean>(false);

  useEffect(() => {
    // Initialize client state with actual online status
    setIsClient(true);
    setIsOnline(navigator.onLine);
    setShowIndicator(false);
    setWasOffline(false);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    const handleOnline = () => {
      setIsOnline(true);
      // Only show "Back Online" if we were previously offline
      if (wasOffline) {
        setShowIndicator(true);
        // Keep showing for 3 seconds before hiding
        setTimeout(() => {
          setShowIndicator(false);
          setWasOffline(false);
        }, 3000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowIndicator(true);
      setWasOffline(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [wasOffline, isClient]);

  if (!showIndicator) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] animate-in slide-in-from-right">
      <Card
        className={`shadow-lg min-w-[280px] ${
          isOnline
            ? "!bg-green border-green-200 border"
            : "bg-red-50 border-red-200 border"
        }`}
      >
        <Flex gap="3" align="center" className="p-2">
          <div
            className={`flex-shrink-0 ${
              isOnline ? "!text-green" : "text-red-600"
            }`}
          >
            {isOnline ? (
              <SVGIcon name="online-status" className="!text-green" />
            ) : (
              <ExclamationTriangleIcon width="24" height="24" />
            )}
          </div>
          <Flex direction="column" gap="1" className="flex-1">
            <Text
              size="2"
              weight="bold"
              className={isOnline ? "!text-green" : "text-red-800"}
            >
              {isOnline ? "Back Online" : "No Internet Connection"}
            </Text>
            <Text
              size="1"
              className={isOnline ? "!text-green" : "text-red-700"}
            >
              {isOnline
                ? "Your internet connection has been restored"
                : "Please check your internet connection"}
            </Text>
          </Flex>
          <Button
            variant="ghost"
            color={isOnline ? "green" : "red"}
            onClick={() => {
              setShowIndicator(false);
            }}
            className="!cursor-pointer"
            aria-label="Close"
          >
            <Cross1Icon width="16" height="16" />
          </Button>
        </Flex>
      </Card>
    </div>
  );
};

export default OfflineIndicator;
